import { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, FileText } from 'lucide-react';
import { Event, EventFormData } from './types';

interface EventFormProps {
  editingEvent?: Event | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function EventForm({ editingEvent, onSuccess, onCancel }: EventFormProps) {
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | ''; text: string }>({ 
    type: '', 
    text: '' 
  });

  // Populate form when editing event
  useEffect(() => {
    if (editingEvent) {
      setFormData({
        title: editingEvent.title || '',
        description: editingEvent.description || '',
      });
    } else {
      setFormData({
        title: '',
        description: '',
      });
    }
  }, [editingEvent]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
    });
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const docData: Record<string, unknown> = {
        title: formData.title,
        description: formData.description,
      };

      if (editingEvent) {
        // Update existing event
        docData.updatedAt = serverTimestamp();
        docData.pondings = editingEvent.pondings || []; // Preserve pondings array
        
        await updateDoc(doc(db, 'events', editingEvent.id), docData);
        setMessage({ 
          type: 'success', 
          text: 'Event updated successfully!' 
        });
      } else {
        // Create new event
        docData.createdAt = serverTimestamp();
        docData.pondings = []; // Initialize empty pondings array
        
        await addDoc(collection(db, 'events'), docData);
        setMessage({ 
          type: 'success', 
          text: 'Event created successfully!' 
        });
      }

      // Reset form and notify parent
      resetForm();
      if (onSuccess) onSuccess();
      
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setMessage({ 
        type: 'error', 
        text: `Error: ${errorMessage}` 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-2xl">
      {message.text && (
        <Card className={`border-2 ${
          message.type === 'success' 
            ? 'border-green-500 bg-green-50' 
            : 'border-red-500 bg-red-50'
        }`}>
          <CardContent className="p-3 flex items-center gap-2">
            <AlertCircle className={`h-4 w-4 ${
              message.type === 'success' ? 'text-green-600' : 'text-red-600'
            } flex-shrink-0`} />
            <span className={`text-xs ${
              message.type === 'success' ? 'text-green-700' : 'text-red-700'
            }`}>
              {message.text}
            </span>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Event Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title" className="text-xs font-medium">
              Event Title *
            </Label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g., Chennai Floods December 2024"
              className="text-sm"
              required
            />
            <p className="text-xs text-muted-foreground">
              A short, descriptive title for this flood event
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-xs font-medium">
              Description *
            </Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe the flood event, affected areas, duration, etc."
              className="text-sm min-h-[120px]"
              required
            />
            <p className="text-xs text-muted-foreground">
              Provide details about the event including location, timeline, and any relevant context
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2 justify-end">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            size="sm"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={loading}
          size="sm"
        >
          {loading ? 'Saving...' : editingEvent ? 'Update Event' : 'Create Event'}
        </Button>
      </div>
    </form>
  );
}
